import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, Eye, ShoppingCart, UserPlus, ArrowLeft, RefreshCw, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_STAGES = [
  { key: 'cinematic_view', label: 'Saw Cinematic', color: 'bg-blue-500' },
  { key: 'cinematic_skip', label: 'Skipped Cinematic', color: 'bg-blue-400' },
  { key: 'scroll_view', label: 'Saw Scroll Section', color: 'bg-indigo-500' },
  { key: 'begin_map', label: 'Entered Map', color: 'bg-violet-500' },
  { key: 'piece_click', label: 'Clicked Puzzle Piece', color: 'bg-purple-500' },
  { key: 'scriptorium_peek', label: 'Saw Book Peek', color: 'bg-fuchsia-500' },
  { key: 'hook_modal_view', label: 'Saw Price Hook', color: 'bg-amber-500' },
  { key: 'hook_buy', label: 'Clicked Buy', color: 'bg-green-500' },
  { key: 'hook_signup', label: 'Signed Up Free', color: 'bg-emerald-500' },
  { key: 'hook_dismiss', label: 'Dismissed', color: 'bg-gray-400' },
];

const PRICE_LABELS = { '3.69': '$3.69', '5.55': '$5.55', '6.39': '$6.39', '7.77': '$7.77' };

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDataset, setShowDataset] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, []);

  const fetchDashboard = useCallback(async (showRefresh) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/dashboard`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (e) {
      toast.error('Error loading analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getHeaders]);

  const fetchEvents = useCallback(async (page = 0) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/events?limit=50&skip=${page * 50}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events);
        setEventsTotal(data.total);
        setEventsPage(page);
      }
    } catch (e) {
      toast.error('Failed to load events');
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  useEffect(() => {
    if (showDataset) fetchEvents(0);
  }, [showDataset, fetchEvents]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchDashboard(true), 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/events/export`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.events.length === 0) { toast.info('No events to export'); return; }
        const keys = Object.keys(data.events[0]);
        const csv = [keys.join(','), ...data.events.map(e => keys.map(k => `"${(e[k] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `discovery_analytics_${new Date().toISOString().split('T')[0]}.csv`;
        a.click(); URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} events`);
      }
    } catch (e) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060503] text-amber-100 flex items-center justify-center">
        <div className="text-amber-600 animate-pulse">Loading analytics...</div>
      </div>
    );
  }

  const d = dashboard;
  const o = d?.overview || {};
  const maxFunnel = Math.max(...FUNNEL_STAGES.map(s => d?.funnel?.[s.key] || 0), 1);

  return (
    <div className="min-h-screen bg-[#060503] text-amber-100">
      {/* Header */}
      <div className="border-b border-amber-900/20 bg-[#0a0704]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-amber-700 hover:text-amber-400 transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-serif text-amber-200" data-testid="analytics-title">Discovery Analytics</h1>
              <p className="text-xs text-amber-700/50">Real-time funnel &amp; pricing data</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={exportCSV} className="text-amber-400 border-amber-800/30 hover:bg-amber-900/20" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchDashboard(true)} className="text-amber-400 border-amber-800/30 hover:bg-amber-900/20" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-cards">
          <Card className="p-4 bg-[#0f0a04] border-amber-900/15">
            <Users className="w-5 h-5 text-blue-400 mb-2" />
            <div className="text-2xl font-bold text-amber-100">{o.total_visitors || 0}</div>
            <div className="text-xs text-amber-700/50">Total Visitors</div>
            <div className="text-[10px] text-amber-600/30 mt-1">{o.today_visitors || 0} today</div>
          </Card>
          <Card className="p-4 bg-[#0f0a04] border-amber-900/15">
            <Eye className="w-5 h-5 text-amber-400 mb-2" />
            <div className="text-2xl font-bold text-amber-100">{d?.funnel?.hook_modal_view || 0}</div>
            <div className="text-xs text-amber-700/50">Hook Views</div>
            <div className="text-[10px] text-amber-600/30 mt-1">{o.today_events || 0} events today</div>
          </Card>
          <Card className="p-4 bg-[#0f0a04] border-amber-900/15">
            <ShoppingCart className="w-5 h-5 text-green-400 mb-2" />
            <div className="text-2xl font-bold text-amber-100">{o.total_conversions || 0}</div>
            <div className="text-xs text-amber-700/50">Conversions</div>
            <div className="text-[10px] text-amber-600/30 mt-1">{o.conversion_rate || 0}% rate</div>
          </Card>
          <Card className="p-4 bg-[#0f0a04] border-amber-900/15">
            <TrendingUp className="w-5 h-5 text-purple-400 mb-2" />
            <div className="text-2xl font-bold text-amber-100">{o.avg_pieces_before_conversion || 0}</div>
            <div className="text-xs text-amber-700/50">Avg Pieces Before Convert</div>
          </Card>
        </div>

        {/* Funnel */}
        <Card className="p-6 bg-[#0f0a04] border-amber-900/15" data-testid="funnel-section">
          <h2 className="text-base font-serif text-amber-200 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            Conversion Funnel
          </h2>
          <div className="space-y-2">
            {FUNNEL_STAGES.map(stage => {
              const count = d?.funnel?.[stage.key] || 0;
              const pct = Math.round((count / maxFunnel) * 100);
              return (
                <div key={stage.key} className="flex items-center gap-3" data-testid={`funnel-${stage.key}`}>
                  <div className="w-36 text-xs text-amber-600/60 text-right shrink-0">{stage.label}</div>
                  <div className="flex-1 h-6 bg-[#0a0704] rounded-md overflow-hidden relative">
                    <div className={`h-full ${stage.color} rounded-md transition-all duration-700`} style={{ width: `${pct}%` }} />
                    <span className="absolute inset-0 flex items-center px-2 text-[11px] font-mono text-amber-100/80">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Price Tier Performance */}
        <Card className="p-6 bg-[#0f0a04] border-amber-900/15" data-testid="price-tiers-section">
          <h2 className="text-base font-serif text-amber-200 mb-4">Price Tier Performance</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-amber-700/50 text-xs">
                  <th className="text-left py-2 px-3">Price</th>
                  <th className="text-center py-2 px-3">Shown</th>
                  <th className="text-center py-2 px-3">Bought</th>
                  <th className="text-center py-2 px-3">Signed Up</th>
                  <th className="text-center py-2 px-3">Dismissed</th>
                  <th className="text-center py-2 px-3">Conv. Rate</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(d?.price_tiers || {}).map(([price, data]) => (
                  <tr key={price} className="border-t border-amber-900/10" data-testid={`price-row-${price}`}>
                    <td className="py-2.5 px-3 font-mono font-bold text-amber-300">{PRICE_LABELS[price] || `$${price}`}</td>
                    <td className="py-2.5 px-3 text-center text-amber-500/70">{data.shown}</td>
                    <td className="py-2.5 px-3 text-center text-green-400 font-semibold">{data.bought}</td>
                    <td className="py-2.5 px-3 text-center text-emerald-400">{data.signed_up}</td>
                    <td className="py-2.5 px-3 text-center text-gray-500">{data.dismissed}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        data.conversion_rate >= 20 ? 'bg-green-900/30 text-green-400' :
                        data.conversion_rate >= 10 ? 'bg-amber-900/30 text-amber-400' :
                        'bg-gray-800/30 text-gray-400'
                      }`}>
                        {data.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Piece Conversions */}
          <Card className="p-6 bg-[#0f0a04] border-amber-900/15" data-testid="piece-conversions">
            <h2 className="text-base font-serif text-amber-200 mb-4">Top Converting Pieces</h2>
            {(d?.piece_conversions?.length || 0) === 0 ? (
              <p className="text-amber-700/30 text-sm">No piece conversion data yet</p>
            ) : (
              <div className="space-y-2">
                {d.piece_conversions.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-amber-400/70 truncate max-w-[200px]">{p.piece}</span>
                    <span className="text-amber-200 font-mono font-bold">{p.conversions}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Source Breakdown */}
          <Card className="p-6 bg-[#0f0a04] border-amber-900/15" data-testid="source-conversions">
            <h2 className="text-base font-serif text-amber-200 mb-4">Conversion by Source</h2>
            {(d?.source_conversions?.length || 0) === 0 ? (
              <p className="text-amber-700/30 text-sm">No source data yet</p>
            ) : (
              <div className="space-y-2">
                {d.source_conversions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-amber-400/70 capitalize">{(s.source || 'unknown').replace('_', ' ')}</span>
                    <span className="text-amber-200 font-mono font-bold">{s.conversions}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Daily Trend */}
        <Card className="p-6 bg-[#0f0a04] border-amber-900/15" data-testid="daily-trend">
          <h2 className="text-base font-serif text-amber-200 mb-4">7-Day Trend</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-amber-700/50 text-xs">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-center py-2 px-3">Visitors</th>
                  <th className="text-center py-2 px-3">Buys</th>
                  <th className="text-center py-2 px-3">Signups</th>
                </tr>
              </thead>
              <tbody>
                {(d?.daily_trend || []).map(day => (
                  <tr key={day.date} className="border-t border-amber-900/10">
                    <td className="py-2 px-3 text-amber-500/70 font-mono text-xs">{day.date}</td>
                    <td className="py-2 px-3 text-center text-amber-200">{day.visitors}</td>
                    <td className="py-2 px-3 text-center text-green-400">{day.buys}</td>
                    <td className="py-2 px-3 text-center text-emerald-400">{day.signups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Raw Dataset Toggle */}
        <Card className="bg-[#0f0a04] border-amber-900/15 overflow-hidden" data-testid="raw-dataset-section">
          <button
            onClick={() => setShowDataset(!showDataset)}
            className="w-full p-6 flex items-center justify-between text-left hover:bg-amber-900/5 transition-colors"
            data-testid="toggle-dataset"
          >
            <div>
              <h2 className="text-base font-serif text-amber-200">Raw Event Dataset</h2>
              <p className="text-xs text-amber-700/40 mt-0.5">{eventsTotal} total events</p>
            </div>
            {showDataset ? <ChevronUp className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-amber-600" />}
          </button>

          {showDataset && (
            <div className="px-6 pb-6">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0f0a04]">
                    <tr className="text-amber-700/50">
                      <th className="text-left py-2 px-2">Time</th>
                      <th className="text-left py-2 px-2">Visitor</th>
                      <th className="text-left py-2 px-2">Event</th>
                      <th className="text-left py-2 px-2">Price</th>
                      <th className="text-left py-2 px-2">Pieces</th>
                      <th className="text-left py-2 px-2">Piece/Passage</th>
                      <th className="text-left py-2 px-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(ev => (
                      <tr key={ev.event_id} className="border-t border-amber-900/5 hover:bg-amber-900/5">
                        <td className="py-1.5 px-2 text-amber-600/50 font-mono whitespace-nowrap">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="py-1.5 px-2 text-amber-500/50 font-mono">{ev.visitor_id?.slice(0, 8)}</td>
                        <td className="py-1.5 px-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            ev.event_type === 'hook_buy' ? 'bg-green-900/30 text-green-400' :
                            ev.event_type === 'hook_signup' ? 'bg-emerald-900/30 text-emerald-400' :
                            ev.event_type === 'hook_dismiss' ? 'bg-gray-800/30 text-gray-400' :
                            ev.event_type === 'hook_modal_view' ? 'bg-amber-900/30 text-amber-400' :
                            'bg-blue-900/20 text-blue-400'
                          }`}>
                            {ev.event_type}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-amber-300 font-mono">{ev.price_shown ? `$${ev.price_shown}` : '-'}</td>
                        <td className="py-1.5 px-2 text-center text-amber-500/50">{ev.pieces_explored ?? '-'}</td>
                        <td className="py-1.5 px-2 text-amber-600/40 truncate max-w-[150px]">{ev.piece_title || '-'}</td>
                        <td className="py-1.5 px-2 text-amber-700/30">{ev.source_page || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {eventsTotal > 50 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-900/10">
                  <span className="text-xs text-amber-700/40">
                    Showing {eventsPage * 50 + 1}-{Math.min((eventsPage + 1) * 50, eventsTotal)} of {eventsTotal}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={eventsPage === 0} onClick={() => fetchEvents(eventsPage - 1)}
                      className="text-amber-400 border-amber-800/30 text-xs">Prev</Button>
                    <Button variant="outline" size="sm" disabled={(eventsPage + 1) * 50 >= eventsTotal} onClick={() => fetchEvents(eventsPage + 1)}
                      className="text-amber-400 border-amber-800/30 text-xs">Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
